/**
 * useTrialPrompts Hook
 *
 * Phase 1.6 - Progressive Trial Prompts State Management
 *
 * Manages the state and logic for progressive trial prompts, tracking analysis
 * count, dismissal states, and persisting to AsyncStorage for consistent UX.
 *
 * Progressive Prompt Strategy (10 weekly analyses):
 * - Prompt 1 (Inline Banner): After 1st analysis (gentle intro)
 * - Prompt 2 (Blur Overlay): At 50% usage (5 analyses used)
 * - Prompt 3 (Bottom Sheet): At 80% usage (8 analyses used)
 *
 * Responsibilities:
 * - Track analysis count for anonymous users
 * - Manage prompt dismissal states (Prompt 1 & 2 are dismissible)
 * - Persist state to AsyncStorage across app sessions
 * - Determine which prompt to show based on quota usage percentage
 *
 * Race Condition Fix (2025-01-15):
 * - Added isMounted cleanup flags to load and save effects
 * - Prevents React warnings from state updates after unmount
 * - Prevents duplicate AsyncStorage writes
 *
 * Usage:
 * ```typescript
 * const {
 *   analysisCount,
 *   showInlineBanner,
 *   showBlurOverlay,
 *   showBottomSheet,
 *   dismissInlineBanner,
 *   dismissBlurOverlay,
 * } = useTrialPrompts({ isAnonymous, analysisData: data, quotaUsed, quotaLimit });
 * ```
 *
 * Created: 2025-01-03
 * Updated: 2025-11-25 - Changed to percentage-based thresholds for 10 weekly analyses
 */

import { useState, useEffect, useRef } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSDUIConfig } from '@dramadetector/ui/json-render';
import { useTrialPromptsFlag } from './useFeatureFlags';

const ASYNC_STORAGE_KEY = '@DramaDetector/trialPromptState';

interface TrialPromptState {
  analysisCount: number;
  hasDismissedBanner: boolean;
  hasDismissedBlur: boolean;
  hasDismissedBottomSheet: boolean;
}

interface UseTrialPromptsProps {
  isAnonymous: boolean;
  analysisData: any; // From useAnalyze hook
  quotaUsed?: number; // Current analyses used (from useUniversalQuota)
  quotaLimit?: number; // Weekly limit (from useUniversalQuota)
}

export function useTrialPrompts({ isAnonymous, analysisData, quotaUsed = 0, quotaLimit = 10 }: UseTrialPromptsProps) {
  // Kill switch: when disabled, all prompt visibility flags resolve to false
  const trialPromptsEnabled = useTrialPromptsFlag();

  // SDUI: prompt thresholds are server-configurable via sdui_surface_configs
  const { config: promptConfig } = useSDUIConfig('consent-modal', isAnonymous ? 'anonymous' : 'free');

  const PROMPT_THRESHOLDS = {
    INLINE_BANNER: promptConfig.inlineBannerThreshold,
    BLUR_OVERLAY: promptConfig.blurOverlayThreshold,
    BOTTOM_SHEET: promptConfig.bottomSheetThreshold,
  };

  const [state, setState] = useState<TrialPromptState>({
    analysisCount: 0,
    hasDismissedBanner: false,
    hasDismissedBlur: false,
    hasDismissedBottomSheet: false,
  });
  const [isLoaded, setIsLoaded] = useState(false);
  const processedAnalysisId = useRef<string | null>(null);

  // Calculate usage percentage for threshold-based prompts
  const usagePercent = quotaLimit > 0 ? Math.round((quotaUsed / quotaLimit) * 100) : 0;

  // Load state from AsyncStorage on initial mount
  useEffect(() => {
    let isMounted = true; // ✅ Cleanup flag

    const loadState = async () => {
      try {
        const storedStateJSON = await AsyncStorage.getItem(ASYNC_STORAGE_KEY);

        if (!isMounted) return; // ✅ Skip state update if unmounted

        if (storedStateJSON) {
          const storedState = JSON.parse(storedStateJSON) as TrialPromptState;
          setState(storedState);
          console.log('[useTrialPrompts] Loaded state from storage:', storedState);
        }
      } catch (error) {
        console.error('[useTrialPrompts] Failed to load state from AsyncStorage:', error);
      } finally {
        if (isMounted) {
          setIsLoaded(true);
        }
      }
    };

    if (isAnonymous) {
      loadState();
    } else {
      // Authenticated users don't need trial prompt state
      setIsLoaded(true);
    }

    return () => {
      isMounted = false; // ✅ Set cleanup flag
    };
  }, [isAnonymous]);

  // Persist state to AsyncStorage whenever it changes
  useEffect(() => {
    if (!isLoaded || !isAnonymous) return;

    let isMounted = true; // ✅ Cleanup flag

    const saveState = async () => {
      try {
        await AsyncStorage.setItem(ASYNC_STORAGE_KEY, JSON.stringify(state));

        if (!isMounted) {
          console.log('[useTrialPrompts] Save completed after unmount (harmless)');
          return;
        }

        console.log('[useTrialPrompts] Saved state to storage:', state);
      } catch (error) {
        console.error('[useTrialPrompts] Failed to save state to AsyncStorage:', error);
      }
    };

    saveState();

    return () => {
      isMounted = false; // ✅ Set cleanup flag
    };
  }, [state, isLoaded, isAnonymous]);

  // Increment analysis count on a new, successful analysis
  useEffect(() => {
    const currentAnalysisId = analysisData?.data?.id;

    if (
      isAnonymous &&
      currentAnalysisId &&
      currentAnalysisId !== processedAnalysisId.current
    ) {
      processedAnalysisId.current = currentAnalysisId;
      setState((prevState) => {
        const newCount = prevState.analysisCount + 1;
        console.log('[useTrialPrompts] Analysis count incremented:', newCount);
        return {
          ...prevState,
          analysisCount: newCount,
        };
      });
    }
  }, [analysisData, isAnonymous]);

  const dismissInlineBanner = () => {
    console.log('[useTrialPrompts] Dismissing inline banner');
    setState((s) => ({ ...s, hasDismissedBanner: true }));
  };

  const dismissBlurOverlay = () => {
    console.log('[useTrialPrompts] Dismissing blur overlay');
    setState((s) => ({ ...s, hasDismissedBlur: true }));
  };

  const dismissBottomSheet = () => {
    console.log('[useTrialPrompts] Dismissing bottom sheet (Maybe Later clicked)');
    setState((s) => ({ ...s, hasDismissedBottomSheet: true }));
  };

  // Determine which prompts to show based on usage percentage thresholds.
  // Only show prompts once state is loaded and for anonymous users.
  //
  // Progressive Prompt Strategy (10 weekly analyses):
  // - Inline Banner: After 1st analysis (10%+ usage) - gentle intro
  // - Blur Overlay: At 50%+ usage (5+ analyses) - medium interruption
  // - Bottom Sheet: At 80%+ usage (8+ analyses) - stronger CTA
  //
  // Each prompt type only shows once per session (tracked by dismissal state).
  // Lower prompts are hidden when higher prompts should show (blur hides banner, sheet hides blur).
  const showInlineBanner =
    trialPromptsEnabled &&
    isLoaded &&
    isAnonymous &&
    usagePercent >= PROMPT_THRESHOLDS.INLINE_BANNER &&
    usagePercent < PROMPT_THRESHOLDS.BLUR_OVERLAY &&
    !state.hasDismissedBanner;

  const showBlurOverlay =
    trialPromptsEnabled &&
    isLoaded &&
    isAnonymous &&
    usagePercent >= PROMPT_THRESHOLDS.BLUR_OVERLAY &&
    usagePercent < PROMPT_THRESHOLDS.BOTTOM_SHEET &&
    !state.hasDismissedBlur;

  const showBottomSheet =
    trialPromptsEnabled &&
    isLoaded &&
    isAnonymous &&
    usagePercent >= PROMPT_THRESHOLDS.BOTTOM_SHEET &&
    !state.hasDismissedBottomSheet;

  return {
    analysisCount: state.analysisCount,
    showInlineBanner,
    showBlurOverlay,
    showBottomSheet,
    dismissInlineBanner,
    dismissBlurOverlay,
    dismissBottomSheet,
    isLoaded, // Expose for conditional rendering during load
    // Expose quota data for UI display
    quotaUsed,
    quotaLimit,
    quotaRemaining: Math.max(0, quotaLimit - quotaUsed),
  };
}
